import { useState } from 'react';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/atoms/tooltip';
import { Checkbox } from '@/components/atoms/checkbox';
import { Input } from '@/components/atoms/input';
import { Slider } from '@/components/atoms/slider';
import { cn } from '@/lib/utils';
import type { SurchargeBase, SurchargeType } from '@/lib/sales/service-rollup';
import type { DynamicServicePricingConfig, ServiceSurchargeConfig } from '@/types/pricebook';

const money = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);

/**
 * What an uplift with no stated base is charged on, per rollUpService. Only
 * uplifts saved before this editor existed are in that state, and they are
 * shown charging all three because that is what they do.
 */
const OMITTED_APPLIES_TO: SurchargeBase = { labor: true, materials: true, equipment: true };

/**
 * A new uplift is charged on labor only.
 *
 * Materials and equipment already earn their profit from the markup profiles;
 * labor has no such mechanism, which is the whole reason this exists. Charging
 * the parts as well would take the same margin twice. The base is written out
 * in full rather than left to the omitted-means-all-three fallback, which would
 * do exactly that.
 */
const NEW_UPLIFT: ServiceSurchargeConfig = {
  type: 'PERCENT',
  value: 0,
  percentBasis: 'MARKUP',
  appliesTo: { labor: true, materials: false, equipment: false },
};

const BASES: Array<{ key: keyof SurchargeBase; label: string }> = [
  { key: 'labor', label: 'Labor' },
  { key: 'materials', label: 'Materials' },
  { key: 'equipment', label: 'Equipment' },
];

/**
 * The profit a time-and-materials service earns on its labor — the labor
 * equivalent of a markup profile.
 *
 * It is stored in the `surcharge*` fields, which predate the name: the same
 * money, on the same base, computed by the same roll-up. They are one feature,
 * not two.
 *
 * `amount` and `base` come from the roll-up that prices the service, so the
 * figure shown here and the money the customer is charged cannot drift apart.
 * Percentage uplifts are always expressed and stored as markup.
 */
export function ProfitUpliftEditor({
  value,
  onChange,
  amount,
  base,
  compact = false,
}: {
  value: DynamicServicePricingConfig;
  onChange: (value: DynamicServicePricingConfig) => void;
  amount: number;
  base: number;
  /**
   * Drop the panel's frame and heading — see `DynamicServicePricingEditor`.
   * Off by default, so existing callers are unchanged.
   */
  compact?: boolean;
}) {
  const surcharge = value.surcharge ?? NEW_UPLIFT;
  const appliesTo = surcharge.appliesTo ?? OMITTED_APPLIES_TO;
  const isPercent = surcharge.type === 'PERCENT';
  const patch = (next: Partial<ServiceSurchargeConfig>) =>
    onChange({ ...value, enabled: true, surcharge: { ...surcharge, ...next } });
  // Flat is dollars and percent is a markup, so the stored number means
  // something different under each type; carrying the old one over would turn a
  // $50 uplift into a 50% one.
  const changeType = (type: SurchargeType) =>
    type === surcharge.type
      ? undefined
      : patch({ type, value: 0, ...(type === 'PERCENT' ? { percentBasis: 'MARKUP' } : {}) });
  const shown = surcharge.value;

  // The typed text is held while it is being typed so an empty box stays empty
  // until the operator enters the next exact figure.
  const [draft, setDraft] = useState<string | null>(null);
  const typed = draft === null ? shown : Number(draft);
  const storeShownValue = (next: number) => {
    if (!Number.isFinite(next)) return;
    patch({ value: next, ...(isPercent ? { percentBasis: 'MARKUP' } : {}) });
  };
  // A slider communicates the ordinary 0–100% range. The adjacent field stays
  // unrestricted for legitimate markups above 100%.
  const sliderMax = 100;
  const sliderValue = Math.min(Math.max(Number.isFinite(typed) ? typed : 0, 0), sliderMax);

  return (
    <div className={compact ? 'space-y-4' : 'space-y-4 rounded-lg border bg-muted/20 p-4'}>
      {compact ? (
        <span className="inline-flex items-center gap-1">
          <span className="text-[11px] font-medium uppercase tracking-[0.04em] text-muted-foreground">
            Profit uplift
          </span>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info size={12} className="cursor-help shrink-0 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[280px]">
              Adds margin on top of labor. Materials and equipment already earn theirs from markup
              profiles. It is carried inside the service price, so the customer sees one price
              rather than a second line.
            </TooltipContent>
          </Tooltip>
        </span>
      ) : (
        <div>
          <h3 className="text-sm font-semibold">Profit uplift</h3>
          <p className="text-xs text-muted-foreground">
            Adds margin on top of labor. Materials and equipment already earn theirs from markup
            profiles. It is carried inside the service price, so the customer sees one price rather
            than a second line.
          </p>
        </div>
      )}

      <div className="grid gap-3 rounded-md border bg-background p-3 sm:grid-cols-2">
        <div className="space-y-1">
          <span className="block text-xs text-muted-foreground">Uplift type</span>
          <div className="flex h-9 shrink-0 items-center gap-0.5">
            {(['PERCENT', 'FLAT'] as const).map((type) => {
              const active = surcharge.type === type;
              return (
                <button
                  key={type}
                  type="button"
                  aria-pressed={active}
                  onClick={() => changeType(type)}
                  className={cn(
                    'rounded px-1.5 py-0.5 text-[11px] font-medium transition-colors',
                    active
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-accent hover:text-foreground',
                  )}
                >
                  {type === 'FLAT' ? 'Flat ($)' : 'Percentage (%)'}
                </button>
              );
            })}
          </div>
        </div>

        <label className="space-y-1">
          <span className="block text-xs text-muted-foreground">
            {isPercent ? 'Markup %' : 'Amount'}
          </span>
          {isPercent ? (
            <div className="flex h-9 items-center gap-3">
              <Slider
                aria-label="Profit uplift percentage slider"
                value={[sliderValue]}
                min={0}
                max={sliderMax}
                step={1}
                onValueChange={([next]) => {
                  setDraft(null);
                  storeShownValue(next);
                }}
                className="flex-1"
              />
              <div className="relative w-20 shrink-0">
                <Input
                  aria-label="Profit uplift percentage"
                  type="number"
                  inputMode="numeric"
                  min="0"
                  step="1"
                  value={draft ?? shown}
                  className="pr-6 text-right"
                  onBlur={() => setDraft(null)}
                  onChange={(event) => {
                    const raw = event.target.value;
                    setDraft(raw);
                    storeShownValue(Number(raw));
                  }}
                />
                <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-muted-foreground">
                  %
                </span>
              </div>
            </div>
          ) : (
            <Input
              aria-label="Profit uplift amount"
              type="number"
              inputMode="numeric"
              min="0"
              step="1"
              value={draft ?? shown}
              onBlur={() => setDraft(null)}
              onChange={(event) => {
                const raw = event.target.value;
                setDraft(raw);
                storeShownValue(Number(raw));
              }}
            />
          )}
        </label>

      </div>

      <div className="space-y-2">
        <span className="block text-xs text-muted-foreground">
          Applies to{!isPercent && ' — a flat uplift is dollars, so it ignores this'}
        </span>
        <div className="flex flex-wrap gap-4">
          {BASES.map(({ key, label }) => (
            <label
              key={key}
              className={cn(
                'flex items-center gap-2 text-xs',
                !isPercent && 'cursor-not-allowed opacity-50',
              )}
            >
              {/* `=== true` is what the roll-up asks of a stated base, so a
                  half-filled one shows exactly what it charges. */}
              <Checkbox
                checked={appliesTo[key] === true}
                disabled={!isPercent}
                onCheckedChange={(checked) =>
                  patch({ appliesTo: { ...appliesTo, [key]: checked === true } })
                }
              />
              {label}
            </label>
          ))}
        </div>
      </div>

      {/* There is deliberately no "what this uplift is for" field. An uplift is
          profit — the business's margin on its own labor — and it does not owe
          anyone a reason. The box invited writing one, and whatever was typed
          then became the row's name wherever the uplift was shown. */}
      <div className="flex items-center justify-between gap-3 border-t pt-2 text-sm">
        <span>
          Profit uplift
          {isPercent && (
            <span className="ml-1 text-xs text-muted-foreground">on {money(base)}</span>
          )}
        </span>
        <strong className="tabular-nums">{money(amount)}</strong>
      </div>
    </div>
  );
}
